import {
    FinancialDataDocument,
    FinancialPeriodMap,
    StatementDump,
    StatementType,
    resolveDefaultStatementType,
    resolveLatestFinancialPeriods,
} from './financialData'
import { CRORE } from '../assets/constants'

export type GrowthChartPeriod = 'annual' | 'quarterly'

export type GrowthChartPoint = {
    period: string
    periodLabel: string
    revenue?: number
    netIncome?: number
    netMargin?: number
}

export type GrowthChartData = {
    points: GrowthChartPoint[]
    hasRevenue: boolean
    hasNetIncome: boolean
    hasNetMargin: boolean
}

const MAX_PERIODS = 5

const REVENUE_METRIC_CANDIDATES = [
    'Sales',
    'Revenue',
    'Total Revenue',
    'Revenue from Operations',
    'Revenue From Operations',
    'Operating Revenue',
    'Interest Earned',
    'Total Income',
]

const NET_INCOME_METRIC_CANDIDATES = [
    'Net profit',
    'Net Profit',
    'Net Profit After Tax',
    'Profit after tax',
    'Profit After Tax',
    'Net income',
    'Net Income',
    'PAT',
]

const NET_MARGIN_METRIC_CANDIDATES = [
    'NPM',
    'Net profit margin',
    'Net Profit Margin %',
    'Net Margin %',
    'Net margin %',
    'NPM %',
    'Profit Margin %',
]

const normalizeMetricKey = (key: string): string => key.trim().toLowerCase()

const FINANCIAL_UNIT_SUFFIX_RE = /^(-?[\d.]+)\s*(cr|l)$/i
const FINANCIAL_TABLE_LCR_RE = /^(-?[\d.]+)\s*l\s*cr\.?$/i
const FINANCIAL_TABLE_CR_RE = /^(-?[\d.]+)\s*cr\.?$/i
const FINANCIAL_TABLE_L_RE = /^(-?[\d.]+)\s*l\.?$/i
const FINANCIAL_TABLE_EMPTY = new Set(['', '—', '-', '–', 'na', 'n/a'])

const trimTrailingZeros = (text: string): string => {
    if (!text.includes('.')) {
        return text
    }
    return text.replace(/\.?0+$/, '')
}

/** Pads fractional values to 2 digits; whole numbers omit trailing .00 */
export const formatConsistentDecimalPlaces = (abs: number, maxDecimals = 2): string => {
    const factor = 10 ** maxDecimals
    const rounded = Math.round(abs * factor) / factor
    if (Number.isInteger(rounded)) {
        return rounded.toLocaleString('en-IN', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })
    }
    return rounded.toLocaleString('en-IN', {
        minimumFractionDigits: maxDecimals,
        maximumFractionDigits: maxDecimals,
    })
}

const formatFixedDecimalPlaces = (abs: number, decimals: number): string =>
    abs.toLocaleString('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })

export type FinancialTableCellFormatOptions = {
    /** When true, every numeric cell in the row uses exactly 2 decimal places. */
    forceTwoDecimals?: boolean
}

/** True when any value in the row has a fractional part at 2-decimal precision. */
export const rowRequiresTwoDecimalPlaces = (values: Array<number | undefined>): boolean =>
    values.some((value) => {
        if (value === undefined || !Number.isFinite(value)) {
            return false
        }
        const rounded = Math.round(Math.abs(value) * 100) / 100
        return !Number.isInteger(rounded)
    })

/** Splits a formatted table cell into the numeric/text portion and optional unit suffix (e.g. " Cr"). */
export const splitFinancialTableCellUnit = (display: string): { value: string; unit?: string } => {
    const match = display.match(/^(.+?)(\s+(?:LCr|Cr)\.?)$/i)
    if (match) {
        return { value: match[1], unit: match[2] }
    }
    return { value: display }
}

export const parseFinancialMetricValue = (value: unknown): number | undefined => {
    if (value === null || value === undefined || value === '') {
        return undefined
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined
    }
    if (typeof value === 'string') {
        const cleaned = value
            .replace(/,/g, '')
            .replace(/%/g, '')
            .replace(/\s*x\s*$/i, '')
            .trim()
        if (!cleaned || cleaned === '—' || cleaned === '-') {
            return undefined
        }
        const unitMatch = cleaned.match(FINANCIAL_UNIT_SUFFIX_RE)
        if (unitMatch) {
            const num = Number(unitMatch[1])
            if (!Number.isFinite(num)) {
                return undefined
            }
            return unitMatch[2].toLowerCase() === 'l' ? num / 100 : num
        }
        const parsed = Number(cleaned)
        return Number.isFinite(parsed) ? parsed : undefined
    }
    return undefined
}

export const formatFinancialCrore = (valueInCrores: number | undefined, precision = 2): string => {
    if (valueInCrores === undefined || !Number.isFinite(valueInCrores)) {
        return '—'
    }
    if (valueInCrores === 0) {
        return '0'
    }
    const sign = valueInCrores < 0 ? '−' : ''
    const abs = Math.abs(valueInCrores)

    if (abs >= 100_000) {
        const lcrDecimals = precision === 0 ? 1 : precision
        const formatted =
            lcrDecimals === 0
                ? String(Math.round(abs / 100_000))
                : precision === 0
                  ? trimTrailingZeros((abs / 100_000).toFixed(lcrDecimals))
                  : formatConsistentDecimalPlaces(abs / 100_000, lcrDecimals)
        return `${sign}${formatted}LCr`
    }

    if (precision === 0) {
        const decimals = abs >= 1000 ? 0 : precision
        const rounded =
            decimals === 0 ? String(Math.round(abs)) : trimTrailingZeros(abs.toFixed(decimals))
        const withCommas = Number(rounded).toLocaleString('en-IN', {
            maximumFractionDigits: decimals,
        })
        return `${sign}${withCommas}Cr`
    }

    const withCommas = formatConsistentDecimalPlaces(abs, precision)
    return `${sign}${withCommas}Cr`
}

const normalizeTableCellText = (text: string): string => text.replace(/,/g, '').replace(/−/g, '-').trim()

const parseTableCellCrores = (text: string): number | undefined => {
    const cleaned = normalizeTableCellText(text)
    const lcrMatch = cleaned.match(FINANCIAL_TABLE_LCR_RE)
    if (lcrMatch) {
        const num = Number(lcrMatch[1])
        return Number.isFinite(num) ? num * 100_000 : undefined
    }
    const crMatch = cleaned.match(FINANCIAL_TABLE_CR_RE)
    if (crMatch) {
        const num = Number(crMatch[1])
        return Number.isFinite(num) ? num : undefined
    }
    const lMatch = cleaned.match(FINANCIAL_TABLE_L_RE)
    if (lMatch) {
        const num = Number(lMatch[1])
        return Number.isFinite(num) ? num / 100 : undefined
    }
    return undefined
}

const formatTablePlainNumber = (
    value: number,
    forRatio = false,
    options?: Pick<FinancialTableCellFormatOptions, 'forceTwoDecimals'>,
): string => {
    const sign = value < 0 ? '−' : ''
    const abs = Math.abs(value)
    if (options?.forceTwoDecimals) {
        const decimals = !forRatio && abs < 1 ? 4 : 2
        return `${sign}${formatFixedDecimalPlaces(abs, decimals)}`
    }
    if (forRatio) {
        return `${sign}${formatConsistentDecimalPlaces(abs, 2)}`
    }
    if (Number.isInteger(value)) {
        return `${sign}${abs.toLocaleString('en-IN', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })}`
    }
    const decimals = abs < 1 ? 4 : 2
    return `${sign}${abs.toLocaleString('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })}`
}

const formatTablePercentDisplay = (
    value: number,
    options?: Pick<FinancialTableCellFormatOptions, 'forceTwoDecimals'>,
): string => {
    const sign = value < 0 ? '−' : ''
    const abs = Math.abs(value)
    const withCommas = options?.forceTwoDecimals
        ? formatFixedDecimalPlaces(abs, 2)
        : formatConsistentDecimalPlaces(abs, 2)
    return `${sign}${withCommas}%`
}

export const formatTableCroreDisplay = (
    valueInCrores: number,
    precision = 2,
    options?: Pick<FinancialTableCellFormatOptions, 'forceTwoDecimals'>,
): string => {
    if (!Number.isFinite(valueInCrores)) {
        return ''
    }
    if (valueInCrores === 0) {
        return options?.forceTwoDecimals ? formatFixedDecimalPlaces(0, precision) : '0'
    }
    const sign = valueInCrores < 0 ? '−' : ''
    const abs = Math.abs(valueInCrores)
    const withCommas = options?.forceTwoDecimals
        ? formatFixedDecimalPlaces(abs, precision)
        : formatConsistentDecimalPlaces(abs, precision)
    return `${sign}${withCommas} Cr`
}

const hasExplicitCroreUnit = (text: string): boolean =>
    parseTableCellCrores(text) !== undefined

/** Plain numeric values at or above 1 Cr (absolute units) are shown in crore format. */
const formatLargePlainNumber = (
    value: number,
    options?: FinancialTableCellFormatOptions,
): string => {
    if (Math.abs(value) >= CRORE) {
        return formatTableCroreDisplay(value / CRORE, 2, options)
    }
    return formatTablePlainNumber(value, false, options)
}

const isPlainNumericText = (text: string): boolean => /^[-\d.,\s]+$/.test(text)

/** Formats financial statement table cells with Indian grouping and unit spacing. */
export const formatFinancialTableCellValue = (
    value: unknown,
    options?: FinancialTableCellFormatOptions,
): string => {
    if (value === null || value === undefined) {
        return ''
    }
    if (typeof value === 'boolean') {
        return String(value)
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? formatLargePlainNumber(value, options) : ''
    }
    if (typeof value === 'string') {
        const trimmed = value.trim()
        if (!trimmed || FINANCIAL_TABLE_EMPTY.has(trimmed.toLowerCase())) {
            return ''
        }

        const croreValue = parseTableCellCrores(trimmed)
        if (croreValue !== undefined) {
            return formatTableCroreDisplay(croreValue, 2, options)
        }

        if (/%/.test(trimmed)) {
            const parsed = parseFinancialMetricValue(trimmed)
            if (parsed !== undefined) {
                return formatTablePercentDisplay(parsed, options)
            }
        }

        if (/x\s*$/i.test(trimmed)) {
            const parsed = parseFinancialMetricValue(trimmed)
            if (parsed !== undefined) {
                return `${formatTablePlainNumber(parsed, true, options)}x`
            }
        }

        const parsed = parseFinancialMetricValue(trimmed)
        if (parsed !== undefined && isPlainNumericText(trimmed) && !hasExplicitCroreUnit(trimmed)) {
            return formatLargePlainNumber(parsed, options)
        }

        return trimmed
    }
    return String(value)
}

/** Parses a financial table cell value back to a chart-ready number (crores for currency metrics). */
export const parseFinancialTableCellNumeric = (value: unknown): number | undefined => {
    if (value === null || value === undefined) {
        return undefined
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return Math.abs(value) >= CRORE ? value / CRORE : value
    }
    if (typeof value === 'string') {
        const trimmed = value.trim()
        if (!trimmed || FINANCIAL_TABLE_EMPTY.has(trimmed.toLowerCase())) {
            return undefined
        }
        const croreValue = parseTableCellCrores(trimmed)
        if (croreValue !== undefined) {
            return croreValue
        }
        if (/%/.test(trimmed)) {
            return parseFinancialMetricValue(trimmed)
        }
        if (hasExplicitCroreUnit(trimmed)) {
            return parseFinancialMetricValue(trimmed)
        }
        const parsed = parseFinancialMetricValue(trimmed)
        if (parsed !== undefined) {
            return parsed
        }
        if (isPlainNumericText(trimmed)) {
            const num = Number(normalizeTableCellText(trimmed))
            return Number.isFinite(num) ? num : undefined
        }
    }
    return parseFinancialMetricValue(value)
}

export const findFinancialMetricValue = (
    periodData: Record<string, unknown> | undefined,
    candidates: string[],
): number | undefined => {
    if (!periodData) {
        return undefined
    }

    const normalizedEntries = Object.entries(periodData).map(([key, value]) => ({
        key,
        normalizedKey: normalizeMetricKey(key),
        value,
    }))

    for (const candidate of candidates) {
        const normalizedCandidate = normalizeMetricKey(candidate)
        const exact = normalizedEntries.find((entry) => entry.normalizedKey === normalizedCandidate)
        const parsed = parseFinancialMetricValue(exact?.value)
        if (parsed !== undefined) {
            return parsed
        }
    }

    return undefined
}

const getStatementPeriodMap = (
    document: FinancialDataDocument,
    periodType: GrowthChartPeriod,
    statementType: StatementType,
): { order: string[]; periodMap: FinancialPeriodMap } => {
    const dump = (
        periodType === 'annual' ? document.annualDataDump : document.quarterlyDataDump
    ) as StatementDump | undefined
    const order = (periodType === 'annual' ? document.annualOrder : document.quarterlyOrder) ?? []
    const periodMap = dump?.[statementType] ?? {}
    return { order, periodMap }
}

const resolveChartPeriods = (order: string[] | undefined, periodMap: Record<string, Record<string, unknown>>): string[] => {
    const latest = resolveLatestFinancialPeriods(order, periodMap, MAX_PERIODS)
    return [...latest].reverse()
}

const extractYear = (period: string): string | undefined => {
    const match = period.match(/\b(19|20)\d{2}\b/)
    return match?.[0]
}

export const formatGrowthChartPeriodLabel = (period: string, periodType: GrowthChartPeriod): string => {
    if (periodType === 'annual') {
        return extractYear(period) ?? period
    }

    const year = extractYear(period)
    const monthMatch = period.match(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\b/i)
    if (monthMatch && year) {
        return `${monthMatch[0].slice(0, 3)} '${year.slice(-2)}`
    }

    return period
}

const computeNetMargin = (revenue?: number, netIncome?: number, directMargin?: number): number | undefined => {
    if (directMargin !== undefined) {
        return directMargin
    }
    if (revenue !== undefined && netIncome !== undefined && revenue !== 0) {
        return (netIncome / revenue) * 100
    }
    return undefined
}

export const buildGrowthProfitabilityChartData = (
    document: FinancialDataDocument,
    periodType: GrowthChartPeriod,
    statementType?: StatementType,
): GrowthChartData => {
    const resolvedStatementType =
        statementType ??
        resolveDefaultStatementType(
            (periodType === 'annual' ? document.annualDataDump : document.quarterlyDataDump) as
                | StatementDump
                | undefined,
        )

    const { order, periodMap } = getStatementPeriodMap(document, periodType, resolvedStatementType)
    const periods = resolveChartPeriods(order, periodMap)

    const ratiosDump = document.ratiosDataDump
    const ratiosPeriodMap = ratiosDump?.[resolvedStatementType] ?? {}

    const points: GrowthChartPoint[] = periods.map((period) => {
        const periodData = periodMap[period] as Record<string, unknown> | undefined
        const ratiosData = ratiosPeriodMap[period] as Record<string, unknown> | undefined

        const revenue = findFinancialMetricValue(periodData, REVENUE_METRIC_CANDIDATES)
        const netIncome = findFinancialMetricValue(periodData, NET_INCOME_METRIC_CANDIDATES)
        const directMargin =
            findFinancialMetricValue(periodData, NET_MARGIN_METRIC_CANDIDATES) ??
            findFinancialMetricValue(ratiosData, NET_MARGIN_METRIC_CANDIDATES)

        return {
            period,
            periodLabel: formatGrowthChartPeriodLabel(period, periodType),
            revenue,
            netIncome,
            netMargin: computeNetMargin(revenue, netIncome, directMargin),
        }
    })

    return {
        points,
        hasRevenue: points.some((point) => point.revenue !== undefined),
        hasNetIncome: points.some((point) => point.netIncome !== undefined),
        hasNetMargin: points.some((point) => point.netMargin !== undefined),
    }
}

export const hasGrowthProfitabilityChartData = (data: GrowthChartData): boolean =>
    data.points.length > 0 && (data.hasRevenue || data.hasNetIncome)
